import { NgClass } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DatePickerComponent } from 'components/date-picker/date-picker.component';
import { ConfirmModalComponent } from 'components/modal/confirm-modal/confirm-modal.component';
import { NgxDropzoneModule } from 'ngx-dropzone';
import { Observable } from 'rxjs';
import { ApiService } from 'services/api.service';
import { AuthorizationService } from 'services/authorization.service';
import { ToastService } from 'services/toast.service';
import { FILE_OBJECT_TYPE_DOCTORAL, FILE_TERM_TYPE_UNKNOWN, FileCard, FileObjectType, FileToAdd } from 'types/file.interface';
import { formatDate } from 'util/date.utility';
import { runFileActionsInOrder } from 'util/file-upload.utility';
import { MAX_LENGTH_NAME } from 'util/form.utility';

export const BYTES_IN_KB: number = 1024;
export const MAX_FILE_SIZE: number = BYTES_IN_KB * BYTES_IN_KB * 10; // 10 MB

export interface FileDescription {
  fileName: string;
  description: string;
  // An uploaded file is identified by its outerId, not its name: the backend allows two files with
  // the same name in one request (scans arrive as IMG_0001.pdf), and keying on the name gave them
  // each other's comments and dropped the survivor's when one was deleted.
  outerId?: string;
}

export interface FileDescriptionToAdd {
  fileName: string;
  description: string;
  termType: string;
  validTill: string;
  backofficeNote: string;
}

@Component({
  selector: 'app-file-upload',
  standalone: true,
  imports: [NgxDropzoneModule, TranslateModule, FormsModule, NgClass, DatePickerComponent, ConfirmModalComponent],
  templateUrl: './file-upload.component.html',
  styleUrl: './file-upload.component.scss'
})
export class FileUploadComponent implements OnChanges {
  public readonly maxDescriptionLength: number = MAX_LENGTH_NAME;
  private _existingFiles: FileCard[] = [];
  @Input()
  set existingFiles(value: FileCard[]) {
    this._existingFiles = (value || []).filter(f => !this.pendingDeletions.includes(f.outerId!));
  }
  get existingFiles(): FileCard[] {
    return this._existingFiles;
  }
  @Input() showDescription: boolean = true;
  @Input() showBackofficeNote: boolean = true;
  @Input() noPadding: boolean = false;
  @Input() isEditMode: boolean = true;
  @Input() showApproveDocumentButton: boolean = false;
  @Input() type: string = FILE_OBJECT_TYPE_DOCTORAL;
  @Input() associatedEntityId!: string;
  @Input() isSubmitting: boolean = false;
  @Input() showTerm: boolean = false;
  @Input() allowToAddNewFile: boolean = false;
  @Input() disableDelete: boolean = false;
  @Input() disableAddFile: boolean = false;
  @Input() maxFiles: number = 0;
  @Output() uploading: EventEmitter<Observable<any>[]> = new EventEmitter<Observable<any>[]>();
  @Input() savedFiles?: { files: File[]; descriptions: FileDescription[] };
  @Output() filesChanged: EventEmitter<{ files: File[]; descriptions: FileDescription[] }> = new EventEmitter();
  @Output() fileDeleted: EventEmitter<void> = new EventEmitter();
  /**
   * Raised only when the operator stages a file, never when `existingFiles` is populated from the
   * server. Deleting a file marked the wizard changed but attaching one did not, so an attachment
   * was discarded on "Iziet" without the unsaved-changes prompt (LNA-1850).
   */
  @Output() filesAdded: EventEmitter<void> = new EventEmitter();
  fileToDelete!: FileCard;
  selectedFileToEdit: FileCard[] = [];
  isOpenConfirmDeleteFile = false;
  public readonly formatDate = formatDate;
  files: File[] = [];
  filesToAdd: any[] = [];
  filesDescriptions: FileDescription[] = [];
  pendingDeletions: string[] = [];
  private savedFilesRestored = false;
  private existingFileIds: Set<string> = new Set();
  private previousFileNotes: Map<string, string> = new Map();
  readonly allowedExtensions: string[] = [
    'doc',
    'dot',
    'docx',
    'dotx',
    'xls',
    'xlsm',
    'xlsx',
    'pdf',
    'rtf',
    'txt',
    'xml',
    'html',
    'mhtml',
    'msg',
    'csv',
    'asice',
    'edoc',
    'jpeg',
    'jpg',
    'gif',
    'png',
    'bmp'
  ];

  constructor(
    private apiService: ApiService,
    private toastService: ToastService,
    private authService: AuthorizationService,
    private translateService: TranslateService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    let restoredFromSaved = false;

    if (changes['savedFiles'] && changes['savedFiles'].currentValue && !this.savedFilesRestored) {
      const saved = changes['savedFiles'].currentValue as { files: File[]; descriptions: FileDescription[] };
      if (saved.files.length > 0 || saved.descriptions.length > 0) {
        const existingNames = new Set((this.existingFiles || []).map(f => f.uploadFileName));
        this.files = saved.files.filter(f => !existingNames.has(f.name));
        this.filesDescriptions = saved.descriptions.map(d => ({ ...d }));
        this.savedFilesRestored = true;
        restoredFromSaved = true;
      }
    }

    if (changes['isSubmitting']) {
      const { currentValue, previousValue } = changes['isSubmitting'];
      if (currentValue === true && previousValue === false) {
        this.handleFileRequests();
      } else if (currentValue === false && previousValue === true) {
        this.files = [];
      }
    }

    if (changes['existingFiles'] && changes['existingFiles'].currentValue) {
      const newIds = new Set((this.existingFiles as FileCard[]).map(f => f.outerId || ''));
      const isSameSet = newIds.size === this.existingFileIds.size && [...newIds].every(id => this.existingFileIds.has(id));

      if (!isSameSet) {
        this.existingFileIds = newIds;

        // Drop staged files the server already returned so the second save doesn't render them twice
        const existingNames = new Set((this.existingFiles as FileCard[]).map(f => f.uploadFileName));
        this.files = this.files.filter(f => !existingNames.has(f.name));
      }

      this.existingFiles.forEach((file: FileCard) => {
        const serverNote = file.fileNote?.note || '';
        const previousServerNote = this.previousFileNotes.get(file.outerId!) ?? null;
        const hasDescription = this.findExistingFileDescription(file);

        if (!hasDescription) {
          this.filesDescriptions.push({
            fileName: file.uploadFileName!,
            description: serverNote,
            outerId: file.outerId!
          });
        } else {
          hasDescription.outerId = file.outerId!;
          if (!restoredFromSaved && previousServerNote !== serverNote) {
            hasDescription.description = serverNote;
          }
        }

        this.previousFileNotes.set(file.outerId!, serverNote);
      });
    }
  }

  onRemoveExistingFile(file: FileCard): void {
    if (!file.outerId) return;
    this.pendingDeletions.push(file.outerId);
    this.existingFiles = this.existingFiles.filter((f) => f.outerId !== file.outerId);
    this.existingFileIds = new Set(this.existingFiles.map(f => f.outerId || ''));
    this.filesDescriptions = this.filesDescriptions.filter((d) => d !== this.findExistingFileDescription(file));
    this.previousFileNotes.delete(file.outerId!);
    this.fileDeleted.emit();
    this.emitFilesChanged();
  }

  onRemoveExistingFileFromAcceptForm(file: FileCard): void {
    this.apiService.deleteFile(file.outerId!).subscribe({
      next: () => {
        this.existingFiles = this.existingFiles.filter((f) => f.outerId !== file.outerId);
        this.isOpenConfirmDeleteFile = false;
        this.toastService.showSuccess('fileDeleteSuccess', '', {
          shouldTranslate: true
        });
      },
      error: () => {
        this.toastService.showRequestError();
        this.isOpenConfirmDeleteFile = false;
      }
    });
  }

  openDeleteConfirmModal(file: FileCard) {
    this.fileToDelete = file;
    this.isOpenConfirmDeleteFile = true;
  }

  onRemove(file: File, inNewFileToAdd?: boolean): void {
    if (inNewFileToAdd) {
      this.filesToAdd = this.filesToAdd.filter((f) => f !== file);
    } else {
      this.files = this.files.filter((f) => f !== file);
      this.filesDescriptions = this.filesDescriptions.filter((description: FileDescription) => description.fileName !== file.name);
      // Removing a file the operator has not uploaded yet is an edit like any other, but only
      // onRemoveExistingFile used to say so — so the wizard never heard about it and "Iziet"
      // discarded the removal without a warning (LNA-1850 F16).
      this.fileDeleted.emit();
      this.emitFilesChanged();
    }
  }

  removeFileByName(fileName: string): void {
    this.files = this.files.filter((f) => f.name !== fileName);
    this.filesDescriptions = this.filesDescriptions.filter((d) => d.fileName !== fileName);
    this.emitFilesChanged();
  }

  clear(): void {
    this.files = [];
    this.filesDescriptions = [];
    this.filesToAdd = [];
    this.pendingDeletions = [];
    this.emitFilesChanged();
  }

  emitFilesChanged(): void {
    this.filesChanged.emit({
      files: [...this.files],
      descriptions: this.filesDescriptions.map(d => ({ ...d }))
    });
  }

  /**
   * LNA-2050 #11. Whether saving now would add, remove or re-describe a file. A save re-sends every
   * stored file's description, so the requests it emits cannot tell; a host that needs to know reads
   * this before it starts the save.
   */
  hasPendingChanges(): boolean {
    return (
      this.files.length > 0 ||
      this.pendingDeletions.length > 0 ||
      this.existingFiles.some(file => this.getExistingFileDescription(file).description !== (file.fileNote?.note || ''))
    );
  }

  checkFiles(hasDuplicate: boolean, hasOversizedFile: boolean, hasInvalidExtension: boolean) {
    if (hasDuplicate) {
      this.toastService.showDanger('duplicateFileError', '', {
        shouldTranslate: true
      });
    }

    if (hasOversizedFile) {
      this.toastService.showDanger('fileTooLargeError', '', {
        shouldTranslate: true
      });
    }

    if (hasInvalidExtension) {
      this.toastService.showDanger('invalidFileTypeError', 'allowedFileTypes', {
        shouldTranslate: true
      });
    }
  }

  get totalFileCount(): number {
    return this.existingFiles.length + this.files.length;
  }

  get isAtFileLimit(): boolean {
    return this.maxFiles > 0 && this.totalFileCount >= this.maxFiles;
  }

  onSelect(event: any): void {
    const newFiles: File[] = event.addedFiles;
    const existingFileNames = this.files.map((file) => file.name);
    const oldFiles = this.existingFiles.map((file) => file.uploadFileName);
    let hitLimit = false;
    let added = false;

    newFiles.forEach((file: File) => {
      const extension = file.name.split('.').pop()?.toLowerCase() || '';
      const isDuplicate = [...existingFileNames, ...oldFiles].includes(file.name);
      const isOversized = file.size > MAX_FILE_SIZE;
      const isInvalidExtension = !this.allowedExtensions.includes(extension);

      if (this.maxFiles > 0 && this.totalFileCount >= this.maxFiles) {
        hitLimit = true;
        return;
      }

      if (!isDuplicate && !isOversized && !isInvalidExtension) {
        this.files.push(file);
        this.filesDescriptions.push({
          fileName: file.name,
          description: ''
        });
        added = true;
      }

      this.checkFiles(isDuplicate, isOversized, isInvalidExtension);
    });

    if (added) {
      this.filesAdded.emit();
    }

    if (hitLimit) {
      const message = this.translateService.instant('components.notification.maxFilesError', { count: this.maxFiles });
      this.toastService.showDanger(message, '', { shouldTranslate: false });
    }

    this.emitFilesChanged();
  }

  onSelectAcceptableFile(event: any) {
    const newFiles: File[] = event.addedFiles;
    const existingFileNames = this.filesToAdd.map((file) => file.name);
    const oldFiles = this.existingFiles.map((file) => file.uploadFileName);

    newFiles.forEach((file: File) => {
      const extension = file.name.split('.').pop()?.toLowerCase() || '';
      const isDuplicate = [...existingFileNames, ...oldFiles].includes(file.name);
      const isOversized = file.size > MAX_FILE_SIZE;
      const isInvalidExtension = !this.allowedExtensions.includes(extension);

      if (!isDuplicate && !isOversized && !isInvalidExtension) {
        this.filesToAdd.push({
          file: file,
          fileObjectType: this.type,
          objectId: this.associatedEntityId,
          name: file.name,
          note: '',
          termType: 'Unknown',
          validTill: '',
          backofficeNote: ' '
        });
      }
      this.checkFiles(isDuplicate, isOversized, isInvalidExtension);
    });
  }

  handleFileRequests(): void {
    const updates = this.existingFiles
      .map((existing: FileCard) => ({ existing, description: this.findExistingFileDescription(existing) }))
      .filter((pair) => pair.description !== undefined);
    // A request carrying a term type replaces the file's whole approval (term, validity date, LNA
    // note), so send the stored one back with the description. Leaving the two out erased the note,
    // and a fixed term without its date was refused, failing the research-topic save (LNA-2046).
    const updateObservables = updates.map(({ existing, description }) =>
      this.apiService.updateFile({
        note: description!.description,
        id: existing.outerId || '',
        backofficeNote: existing.fileNote?.backofficeNote ?? null,
        validTill: existing.fileNote?.validTill ?? null,
        termType: existing.fileNote?.termType || FILE_TERM_TYPE_UNKNOWN
      })
    );

    // Prepare observables for file uploads
    const uploadObservables = this.files.map((file: File) =>
      this.apiService.uploadFile(
        file,
        this.type,
        this.associatedEntityId,
        this.getFileDescription(file.name).description,
        FILE_TERM_TYPE_UNKNOWN
      )
    );

    const deleteObservables = this.pendingDeletions.map(id => this.apiService.deleteFile(id));
    this.pendingDeletions = [];

    const rest = [...uploadObservables, ...updateObservables];

    // Replacing a file at the cap is a delete plus an upload of the same kind. The hosts `forkJoin`
    // whatever they are handed, so running both sides at once raced the server's live count per
    // (request, kind): the upload came back 400 while the delete had already gone through, and
    // `forkJoin` cancelled the siblings — so the operator silently lost both files (LNA-1850).
    // Chain instead: deletions first, uploads and description updates only once they have
    // committed. The emission stays an array of observables, so every consumer keeps working. The
    // chain lets every action finish, as a host's `isolateFileActions` cannot reach inside it (LNA-2047).
    if (deleteObservables.length === 0 || rest.length === 0) {
      this.uploading.emit([...deleteObservables, ...rest]);
      return;
    }

    this.uploading.emit([runFileActionsInOrder(deleteObservables, rest)]);
  }

  downloadFile(file: FileCard | any): void {
    this.apiService.downloadFile(file.outerId!).subscribe(
      (data) => {
        const blob = new Blob([data], { type: 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.uploadFileName!;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      },
      (error) => {
        console.error(error);
        this.toastService.showRequestError();
      }
    );
  }

  getFileDescription(fileName: string): FileDescription {
    return (
      this.filesDescriptions.find((description: FileDescription) => description.fileName === fileName) || { fileName: '', description: '' }
    );
  }

  getExistingFileDescription(file: FileCard): FileDescription {
    return this.findExistingFileDescription(file) || { fileName: file.uploadFileName!, description: '' };
  }

  private findExistingFileDescription(file: FileCard): FileDescription | undefined {
    const byId = this.filesDescriptions.find((description: FileDescription) => !!file.outerId && description.outerId === file.outerId);
    if (byId) return byId;
    // A description restored from the wizard's saved state carries no id until the server card
    // arrives, so fall back to the name for the descriptions that have not been claimed yet.
    return this.filesDescriptions.find((description: FileDescription) => !description.outerId && description.fileName === file.uploadFileName);
  }

  onEditFileNote(file: FileCard): void {
    this.selectedFileToEdit.push(file);
  }

  cancelEdit(file: FileCard): void {
    this.selectedFileToEdit = this.selectedFileToEdit.filter((f) => f.outerId !== file.outerId);
  }

  onModalClose(): void {
    this.isOpenConfirmDeleteFile = false;
  }

  onDateSelected(date: string, file: FileCard) {
    if (file.fileNote) {
      file.fileNote.validTill = date;
      file.fileNote.termType = 'Fixed';
    } else {
      file.fileNote = { termType: 'Fixed', validTill: date, note: '' };
    }
  }

  updateFileIndefinity(file: FileCard, checked: any): void {
    if (!file.fileNote) {
      file.fileNote = { note: '', termType: 'Unknown', validTill: null };
    }

    file.fileNote.termType = checked ? 'Indefinite' : 'Unknown';
    file.fileNote.validTill = '';
  }

  confirmFile(file: FileCard): void {
    if (!(file.fileNote.termType === 'Indefinite' || file.fileNote.validTill)) {
      this.toastService.showDanger('fileConfirmDanger', '', {
        shouldTranslate: true
      });
      return;
    }
    const description = this.getExistingFileDescription(file).description;

    const payload = {
      id: file.outerId!,
      note: description,
      backofficeNote: file.fileNote?.backofficeNote || null,
      validTill: file.fileNote?.termType === 'Fixed' ? file.fileNote.validTill : null,
      termType: file.fileNote?.termType || 'Unknown'
    };

    this.apiService.updateFile(payload).subscribe({
      next: () => {
        this.toastService.showSuccess('fileConfirmSuccess', '', {
          shouldTranslate: true
        });

        this.selectedFileToEdit = this.selectedFileToEdit.filter((f) => f.outerId !== file.outerId);
      },
      error: () => {
        this.toastService.showRequestError();
      }
    });
  }

  isFileExpired(file: FileCard): boolean {
    const note = file.fileNote;

    if (note?.termType === 'Fixed' && note.validTill) {
      const validTillDate = new Date(note.validTill);
      const today = new Date();

      validTillDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);

      return validTillDate < today;
    }

    return false;
  }

  isFileValid(file: any): boolean {
    return file.termType === 'Indefinite' || !!file.validTill;
  }

  confirmFileForNewFile(file: FileToAdd): void {
    const fileToUpload = this.filesToAdd.find((f) => f.name === file.name);
    if (!fileToUpload) return;

    if (!(fileToUpload.termType === 'Indefinite' || fileToUpload.validTill)) {
      this.toastService.showDanger('fileConfirmDanger', '', {
        shouldTranslate: true
      });
      return;
    }

    this.apiService
      .uploadFile(
        fileToUpload.file,
        this.type,
        this.associatedEntityId,
        fileToUpload.description,
        fileToUpload.termType,
        fileToUpload.validTill,
        fileToUpload.backofficeNote
      )
      .subscribe({
        next: (response) => {
          const fileId = response;
          const now = new Date().toISOString();
          const newFileCard: FileCard = {
            outerId: fileId,
            objectId: this.associatedEntityId,
            objectType: this.type as FileObjectType,
            uploadFileName: fileToUpload.name,
            mimeType: fileToUpload.file.type,
            uploadedBy: this.authService.getUserName()!,
            uploadedOn: now,
            length: fileToUpload.file.size,
            fileNote: {
              note: fileToUpload.description || null,
              backofficeNote: fileToUpload.backofficeNote || null,
              validTill: fileToUpload.termType === 'Fixed' ? fileToUpload.validTill : null,
              termType: fileToUpload.termType
            }
          };

          this.existingFiles.push(newFileCard);
          this.filesDescriptions.push({
            fileName: file.name,
            description: file.description,
            outerId: fileId
          });

          this.filesToAdd = this.filesToAdd.filter((f) => f.name !== fileToUpload.name);

          this.toastService.showSuccess('fileConfirmSuccess', '', {
            shouldTranslate: true
          });
        },
        error: (err) => {
          if (this.isDuplicateFileError(err)) {
            this.toastService.showDanger('duplicateFileError', '', { shouldTranslate: true });
          } else {
            this.toastService.showRequestError();
          }
        }
      });
  }

  updateFileIndefinityForNewFile(file: FileToAdd, checked: any) {
    file.termType = checked ? 'Indefinite' : 'Unknown';
    file.validTill = '';
  }

  onDateSelectedForNewFile(date: string, file: FileToAdd) {
    file.validTill = date;
    file.termType = 'Fixed';
  }

  private isDuplicateFileError(err: any): boolean {
    const errors = err?.error?.errors;
    if (!errors) return false;
    return Object.values(errors).some(
      (msgs: any) => Array.isArray(msgs) && msgs.some((m: string) => m === 'Such file already exists.')
    );
  }

  convertBytesToMB(bytes: number): string {
    if (bytes < BYTES_IN_KB) {
      return `${Math.ceil(bytes)} B`;
    } else if (bytes < BYTES_IN_KB * BYTES_IN_KB) {
      return `${Math.ceil(bytes / BYTES_IN_KB)} KB`;
    } else {
      return `${Math.ceil(bytes / BYTES_IN_KB / BYTES_IN_KB)} MB`;
    }
  }
}
